import type { BlockGuide } from "./types";

export const guide: BlockGuide = {
  slug: "whatsapp",
  app: "WhatsApp",
  title: "How to limit WhatsApp without disappearing on people",
  h1: "How to limit WhatsApp",
  description:
    "Why blocking WhatsApp is usually the wrong answer, what to do about the group chats instead, and the Screen Time settings that help without cutting you off.",
  targets: [
    "how to block whatsapp on iphone",
    "whatsapp group chats too much",
    "how to limit whatsapp notifications",
    "whatsapp screen time",
  ],
  published: "2026-09-03",
  standfirst:
    "This is the one page here that mostly argues against blocking. WhatsApp is where your family is, and the problem is almost never the app.",
  takeaways: [
    "Blocking a messaging app that people rely on to reach you is a cost paid by other people, which is a different thing from the rest of this list.",
    "The problem is nearly always specific group chats, and those can be muted individually and permanently.",
    "Turn off message previews on the lock screen. Most WhatsApp opens are triggered by a preview rather than a decision.",
  ],
  mechanic: [
    "WhatsApp is not designed to hold your attention. There is no feed, no recommendation engine, no infinite anything — and in most of the world it is simply the phone network, which means blocking it is closer to unplugging a landline than to deleting a social app. Any advice that treats it like TikTok is advice from somewhere WhatsApp is optional.",
    "What it does have is groups, and groups scale badly. A forty-person school group produces a conversation that is continuous, mostly irrelevant to you, and impossible to catch up on — while carrying a genuine obligation, because the one message in two hundred that matters is addressed to you. That combination is what makes people describe WhatsApp as exhausting even though nothing in it is trying to be.",
    "The other mechanic is the preview. A message arriving on the lock screen shows enough to be legible and not enough to be finished, so the phone is picked up to complete a sentence — and the pickup is where everything else on the phone gets its chance. WhatsApp is frequently not the app that takes the twenty minutes; it is the app that opens the phone.",
  ],
  limitFails:
    "A limit on WhatsApp fails in a way that is worth taking seriously: it works, and the cost lands on somebody else. Messages go unread, a family member cannot reach you, and the person who set the limit ends up removing it after a genuinely urgent thing was missed. Screen Time is also measuring minutes for a problem that is made of interruptions — the twenty seconds spent replying is not the cost, the broken concentration around it is, and no time budget can see that.",
  instead: [
    "Mute the worst groups for a year. WhatsApp offers exactly that option and almost nobody uses it; it stops the notifications and leaves the messages there.",
    "Turn off lock-screen previews: Settings → Notifications → WhatsApp → Show Previews → Never. You still see that something arrived, without the half-sentence that pulls the phone open.",
    "Leave the groups you were added to and have never posted in. Doing it quietly is fine.",
    "If you need a genuine gap — an evening, a Sunday — use Downtime with Messages and Phone in Always Allowed, so people can still reach you urgently.",
  ],
  verses: [
    {
      verse: {
        text: "So, then, my beloved brothers, let every man be swift to hear, slow to speak, and slow to anger; for the anger of man doesn't produce the righteousness of God.",
        ref: "James 1:19–20",
      },
      note: "Written long before group chats and describing them precisely. The order — hearing first, speaking second — is the thing a forty-person thread inverts, and most of the damage done in family group chats is done by somebody replying quickly to something they had not finished reading.",
    },
    {
      verse: {
        text: "Set a watch, Yahweh, before my mouth. Keep the door of my lips.",
        ref: "Psalm 141:3",
      },
      note: "A request for a guard on what leaves rather than on what enters, which makes it the right verse for messaging rather than for feeds. Note that the psalmist does not ask to be made into someone who never says the wrong thing; he asks for a sentry, having presumably established that he cannot be trusted with the door himself.",
    },
  ],
  body: [
    {
      type: "h2",
      text: "This one is different",
      id: "this-one-is-different",
    },
    {
      type: "p",
      text: "Most of these pages are about an app engineered to hold you. WhatsApp is not that, and pretending otherwise produces advice people correctly ignore. In much of the world it is how appointments are made, schools communicate and families exist.",
    },
    {
      type: "p",
      text: "So the question is not how to block it. It is how to stop a small number of groups making a communication tool feel like a job.",
    },
    {
      type: "h2",
      text: "Mute for a year",
      id: "mute-for-a-year",
    },
    {
      type: "p",
      text: "WhatsApp's mute options include a year, which is effectively permanent and reversible. Applied to the three loudest groups it removes most of the noise while leaving you reachable and leaving the messages available when you want them.",
    },
    {
      type: "note",
      text: "Nobody is told that you muted a group. This is the least socially expensive intervention on the entire site.",
    },
    {
      type: "h2",
      text: "Previews open the phone",
      id: "previews-open-the-phone",
    },
    {
      type: "p",
      text: "A half-visible message is an unfinished sentence, and unfinished sentences are extremely hard to leave. Turning previews off converts a compulsion into a decision — you know something arrived and you choose when to read it.",
    },
    {
      type: "p",
      text: "It also removes one of the most common ways an evening starts: a message glanced at, a phone unlocked, and forty minutes somewhere {entirely different}.",
      links: {
        "entirely different": "/blog/how-to-stop-scrolling-at-night",
      },
    },
  ],
  faq: [
    {
      q: "Should I block WhatsApp?",
      a: "Usually not. It is a communication tool people rely on to reach you, so blocking it moves the cost onto them. Mute the loud groups and turn off lock-screen previews instead.",
    },
    {
      q: "How do I mute a WhatsApp group permanently?",
      a: "Open the chat, tap the name, choose Mute and select a year. It is reversible, nobody is notified, and it is the single most effective thing on this page.",
    },
    {
      q: "How do I stop WhatsApp interrupting my evening?",
      a: "Turn off Show Previews for it in iOS Notifications, and use Downtime with Messages and Phone in Always Allowed so genuinely urgent contact still gets through.",
    },
  ],
  relatedApps: ["discord", "snapchat"],
  related: ["phone-rules-for-christian-families", "digital-sabbath"],
  relatedPrayers: ["prayer-for-focus", "prayer-before-bed"],
};
